/**
 * Build Information - Cryptographically Tied to Deploy Artifact
 * 
 * This module provides build metadata injected at compile time by Vite.
 * Values are set during the build process and cannot be modified at runtime.
 * 
 * Purpose: Prove that the live site is running the exact commit that was built and deployed.
 */

// These constants are injected by Vite at build time (see vite.config.mjs)
declare const __GIT_SHA__: string;
declare const __BUILD_TIME_UTC__: string;
declare const __APP_VERSION__: string;
declare const __GIT_BRANCH__: string;

/**
 * Full Git commit SHA (40 characters)
 * Injected from: git rev-parse HEAD
 */
export const GIT_SHA = typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : 'unknown';

/**
 * Short Git commit SHA (7 characters)
 * Used for display purposes
 */
export const GIT_SHA_SHORT = GIT_SHA.substring(0, 7);

/**
 * ISO 8601 UTC timestamp of when the build was created
 * Format: YYYY-MM-DDTHH:mm:ss.sssZ
 */
export const BUILD_TIME_UTC = typeof __BUILD_TIME_UTC__ !== 'undefined' ? __BUILD_TIME_UTC__ : 'unknown';

/**
 * Semantic version from package.json
 * Format: MAJOR.MINOR.PATCH (e.g., "1.7.7")
 */
export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';

/**
 * Git branch name at build time
 */
export const GIT_BRANCH = typeof __GIT_BRANCH__ !== 'undefined' ? __GIT_BRANCH__ : 'unknown';

/**
 * Full build tag for display
 * Format: "v{version} ({shortSHA} {timestamp})"
 * Example: "v1.7.7 (f232e28 2025-12-29T16:43:58Z)"
 */
export const BUILD_TAG = `v${APP_VERSION} (${GIT_SHA_SHORT} ${BUILD_TIME_UTC})`;

/**
 * Environment indicator (set via VITE_ENV or defaults to production)
 */
export const ENVIRONMENT = import.meta.env.VITE_ENV || 'production';

/**
 * Complete build information object
 */
export const BUILD_INFO = {
  gitSha: GIT_SHA,
  gitShaShort: GIT_SHA_SHORT,
  gitBranch: GIT_BRANCH,
  buildTimeUtc: BUILD_TIME_UTC,
  appVersion: APP_VERSION,
  buildTag: BUILD_TAG,
  environment: ENVIRONMENT,
} as const;
